import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import moment from "moment";
import { i18nextIDBBackend } from "./services/i18nextIDBBackend";
import { refreshCachesOnBuildChange } from "./services/cacheBuildRefresh";
import { getI18nextNamespaces } from "./services/apiCacheConfig";
import {
  areAllNamespacesCached,
  getI18nextLoadPath,
  normalizeLanguage,
  preloadResourcesFromIDB,
} from "./services/i18nextIDBCache";

export const I18N_NAMESPACES = getI18nextNamespaces();

function getInitialLanguage() {
  return normalizeLanguage(
    localStorage.getItem("i18nextLng") ||
    navigator.language ||
    "en"
  );
}

export const i18nReady = (async () => {
  await refreshCachesOnBuildChange();
  const lng = getInitialLanguage();
  const preloadedResources = await preloadResourcesFromIDB(lng, I18N_NAMESPACES);
  const fullyCached = areAllNamespacesCached(lng, I18N_NAMESPACES, preloadedResources);

  await new Promise((resolve, reject) => {
    i18next
      .use(i18nextIDBBackend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init(
        {
          lng,
          react: {
            useSuspense: false,
          },
          load: "languageOnly",
          nonExplicitSupportedLngs: true,
          interpolation: {
            formatSeparator: ",",
            format: function (value, formatting, language) {
              moment.locale(language);
              if (language === "en") {
                return moment.unix(value / 1000).format("MMM Do, YYYY");
              }
              if (language === "es") {
                return moment.unix(value / 1000).format("DD [de] MMM YYYY");
              }
              return moment.unix(value / 1000).format("MMM Do, YYYY");
            },
          },
          initImmediate: false,
          fallbackLng: "en",
          ns: I18N_NAMESPACES,
          defaultNS: "default",
          fallbackNS: [
            "header",
            "parties",
            "lineItem",
            "notes",
            "valueBuildUp",
            "state_text",
            "transport",
          ],
          supportedLngs: ["en", "es", "de", "nl"],
          // false when every namespace is already in resources → i18next skips backend entirely on init
          partialBundledLanguages: !fullyCached,
          resources: preloadedResources,
          detection: {
            order: ["localStorage", "navigator"],
            caches: ["localStorage"],
            lookupLocalStorage: "i18nextLng",
          },
          backend: {
            loadPath: getI18nextLoadPath(),
          },
        },
        (err) => {
          if (err) reject(err);
          else resolve();
        }
      );
  });

  // Allow loading other languages later (e.g. user switches EN → ES in settings)
  if (fullyCached) {
    i18next.options.partialBundledLanguages = true;
  }

  return i18next;
})();

export default i18next;
